import { reactive } from 'vue'

// 自定义WebSocket Hook，支持传入配置参数
const useWebSocket = (options = {}) => {
  // 默认配置
  const defaultOptions = {
    baseUrl: 'ws://localhost:8081/api/websocket/',
    maxReconnectInterval: 30000, // 最大重连间隔(ms)
    maxReconnectCount: 10, // 最大重连次数，超过后停止重连
    maxReconnectTime: 60000, // 最大重连时间(ms)，超过后停止重连
    // defaultMsgParam未设置时的处理分析
    // defaultMsgParam: '554073095' // 当localStorage中没有msg参数时使用的默认值
  }
  
  // 合并配置
  const opts = { ...defaultOptions, ...options }

  // 创建响应式状态
  const state = reactive({
    socket: null,               // WebSocket实例
    isConnected: false,         // 是否已连接
    isConnecting: false,        // 是否正在连接中
    lastError: null,            // 最后一次错误信息
    reconnectCount: 0,          // 当前重连次数
    messageQueue: [],           // 待发送消息队列
    reconnectStartTime: null,   // 重连开始时间
    reconnectTimer: null        // 重连定时器
  })

  // 连接WebSocket
  const connect = () => {
    // 防止重复连接
    if (state.isConnecting || state.isConnected) return
    
    // 重置连接状态
    state.isConnecting = true
    state.lastError = null

    // 记录重连开始时间（如果是首次连接或重连）
    if (!state.reconnectStartTime) {
      state.reconnectStartTime = Date.now()
    }

    // 检查是否超过最大重连时间
    if (state.reconnectStartTime && 
        Date.now() - state.reconnectStartTime > opts.maxReconnectTime) {
      console.log(`已超过最大重连时间(${opts.maxReconnectTime}ms)，停止重连`)
      window.dispatchEvent(new CustomEvent('websocket-timeout', {
        detail: { maxTime: opts.maxReconnectTime }
      }))
      disconnect()
      return
    }

    // 关闭已存在的连接
    if (state.socket) {
      disconnect()
    }

    // 构建连接地址（带参数编码）
    const msgParam = localStorage.getItem('msg') || opts.defaultMsgParam
    
    // 检查msgParam是否存在
    if (!msgParam) {
      console.error('缺少必要的msg参数，无法建立WebSocket连接');
      
      // 记录重连开始时间（如果尚未设置）
      if (!state.reconnectStartTime) {
        state.reconnectStartTime = Date.now();
      }
      
      // 检查是否超过最大重连时间
      if (state.reconnectStartTime && 
          Date.now() - state.reconnectStartTime > opts.maxReconnectTime) {
        console.log(`已超过最大重连时间(${opts.maxReconnectTime}ms)，停止重连`);
        window.dispatchEvent(new CustomEvent('websocket-timeout', { 
          detail: { maxTime: opts.maxReconnectTime } 
        }));
        disconnect();
        return;
      }
      
      // 触发参数缺失事件
      window.dispatchEvent(new CustomEvent('websocket-missing-param', { 
        detail: { param: 'msg' } 
      }));
      
      // 尝试重连
      if (state.reconnectCount < opts.maxReconnectCount) {
        const interval = Math.min(
          1000 * Math.pow(2, state.reconnectCount), 
          opts.maxReconnectInterval
        );
        
        state.reconnectCount++;
        console.log(`第${state.reconnectCount}次重连将在${interval}ms后尝试...`);
        
        // 存储定时器引用以便后续可能的取消
        state.reconnectTimer = setTimeout(connect, interval);
      } else {
        console.log(`已达到最大重连次数(${opts.maxReconnectCount})，停止重连`);
        window.dispatchEvent(new CustomEvent('websocket-max-reconnect', { 
          detail: { maxCount: opts.maxReconnectCount } 
        }));
      }
      
      state.isConnecting = false;
      return;
    }
    
    const encodedParam = encodeURIComponent(msgParam)
    const url = opts.baseUrl + encodedParam

    try {
      // 创建原生WebSocket实例
      state.socket = new WebSocket(url)

      // 连接成功回调
      state.socket.onopen = () => {
        console.log('WebSocket连接成功')
        state.isConnected = true
        state.isConnecting = false
        state.reconnectCount = 0 // 重置重连次数
        
        // 发送队列中的消息
        flushMessageQueue()
        
        // 触发连接成功事件
        window.dispatchEvent(new CustomEvent('websocket-connected'))
      }

      // 接收消息回调
      state.socket.onmessage = (event) => {
        console.log('后端返回: ', event.data)
        try {
          // 尝试解析为JSON
          const payload = JSON.parse(event.data)
          // 触发全局事件
          const customEvent = new CustomEvent('global-notification', { detail: payload })
          window.dispatchEvent(customEvent)
        } catch (e) {
          // 解析失败时包装为文本消息
          console.warn('消息不是JSON格式，将原始内容作为消息发送:', e)
          const payload = {
            type: 'text',
            content: event.data,
            timestamp: Date.now()
          }
          const customEvent = new CustomEvent('global-notification', { detail: payload })
          window.dispatchEvent(customEvent)
        }
      }

      // 连接错误回调
      state.socket.onerror = (error) => {
        console.error('WebSocket错误:', error)
        state.lastError = error
        state.isConnecting = false
        
        // 触发错误事件
        window.dispatchEvent(new CustomEvent('websocket-error', { detail: error }))
      }

      // 连接关闭回调
      state.socket.onclose = (event) => {
        console.log(`WebSocket关闭，代码: ${event.code}, 原因: ${event.reason}`)
        
        // 更新状态
        const wasConnected = state.isConnected
        state.isConnected = false
        state.isConnecting = false

        // 触发关闭事件
        window.dispatchEvent(new CustomEvent('websocket-closed', { 
          detail: { code: event.code, reason: event.reason } 
        }))

        // 非主动关闭且未超过最大重连次数和时间时尝试重连
    if (event.code !== 1000 && state.reconnectCount < opts.maxReconnectCount) {
      // 检查是否超过最大重连时间
      if (state.reconnectStartTime && 
          Date.now() - state.reconnectStartTime > opts.maxReconnectTime) {
        console.log(`已超过最大重连时间(${opts.maxReconnectTime}ms)，停止重连`)
        window.dispatchEvent(new CustomEvent('websocket-timeout', {
          detail: { maxTime: opts.maxReconnectTime }
        }))
        return
      }

      // 计算重连间隔（指数退避）
      const interval = Math.min(
        1000 * Math.pow(2, state.reconnectCount), 
        opts.maxReconnectInterval
      )
      
      state.reconnectCount++
      console.log(`第${state.reconnectCount}次重连将在${interval}ms后尝试...`)
      
      // 存储定时器引用以便后续可能的取消
      state.reconnectTimer = setTimeout(connect, interval)
    } else if (state.reconnectCount >= opts.maxReconnectCount) {
      console.log(`已达到最大重连次数(${opts.maxReconnectCount})，停止重连`)
      window.dispatchEvent(new CustomEvent('websocket-max-reconnect', {
        detail: { maxCount: opts.maxReconnectCount }
      }))
    }
      }
    } catch (error) {
      console.error('创建WebSocket实例失败:', error)
      state.lastError = error
      state.isConnecting = false
      window.dispatchEvent(new CustomEvent('websocket-error', { detail: error }))
    }
  }

  // 断开连接
  const disconnect = () => {
    // 清除重连定时器
    if (state.reconnectTimer) {
      clearTimeout(state.reconnectTimer)
      state.reconnectTimer = null
    }

    if (state.socket) {
      // 移除所有事件监听
      state.socket.onopen = null
      state.socket.onmessage = null
      state.socket.onerror = null
      state.socket.onclose = null
      
      // 主动关闭连接
      state.socket.close(1000, '正常关闭')
      state.socket = null
    }
    
    // 更新状态
    state.isConnected = false
    state.isConnecting = false
    state.reconnectCount = 0 // 重置重连次数
    state.reconnectStartTime = null // 重置重连开始时间
  }

  // 发送消息
  const sendMessage = (message) => {
    if (state.isConnected && state.socket) {
      try {
        const data = typeof message === 'object'
          ? JSON.stringify(message)
          : message
        state.socket.send(data)
        return true
      } catch (e) {
        console.error('发送消息失败:', e)
        // 发送失败时将消息加入队列
        enqueueMessage(message)
        return false
      }
    } else {
      console.warn('WebSocket未连接，将消息加入队列')
      // 未连接时将消息加入队列
      enqueueMessage(message)
      return false
    }
  }

  // 将消息加入队列
  const enqueueMessage = (message) => {
    state.messageQueue.push(message)
  }

  // 发送队列中的所有消息
  const flushMessageQueue = () => {
    if (!state.isConnected || !state.socket) {
      console.warn('WebSocket未连接，无法发送队列中的消息')
      return
    }

    console.log(`开始发送队列中的${state.messageQueue.length}条消息`)
    while (state.messageQueue.length > 0) {
      const message = state.messageQueue.shift()
      try {
        const data = typeof message === 'object'
          ? JSON.stringify(message)
          : message
        state.socket.send(data)
        console.log('队列消息发送成功')
      } catch (e) {
        console.error('队列消息发送失败:', e)
        // 发送失败时重新加入队列
        state.messageQueue.unshift(message)
        break
      }
    }
    console.log(`消息队列处理完成，剩余${state.messageQueue.length}条消息`)
  }

  return { 
    state, 
    connect, 
    disconnect,
    sendMessage
  }
}

export default useWebSocket
